import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Backdrop, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, InputLabel, List, ListItem, ListItemButton, ListItemText, MenuItem, Popper, Select, Snackbar, Switch, TextField, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import CategoryIcon from '@mui/icons-material/Category';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import Autocomplete from '@mui/material/Autocomplete';
import Page from 'src/components/Page';
import AlertScreen from 'src/components/Alert';
import DosyaYukleyici, { DOSYA_TIPLERI, tipEksikVar } from 'src/components/DosyaYukleyici';
const dosyaTipiLabel = t => (DOSYA_TIPLERI.find(x => x.value === t) || { label: 'Diğer' }).label;
import axios from 'src/utils/axios';

const DOSYA_HINT = 'Ekipman fotoğrafı, kalibrasyon belgesi, ekipman üzerindeki etiket, fatura görseli vb. güncel dökümanlar eklenebilir.';

const styles = {
  root: (theme) => ({
    padding: theme.spacing(3)
  }),
  toolbar: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2)
  }),
  spacer: {
    flexGrow: 1
  },
  ustRow: (theme) => ({
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    marginTop: theme.spacing(0.5)
  }),
  grupRow: (theme) => ({
    paddingLeft: theme.spacing(5)
  }),
  ekipmanRow: (theme) => ({
    paddingLeft: theme.spacing(9)
  }),
  passive: {
    opacity: 0.5
  },
  stokChip: (theme) => ({
    marginLeft: theme.spacing(1)
  }),
  uzmanlikGroup: (theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
    padding: '2px 8px 4px',
    marginTop: '4px',
    borderRadius: 12,
    backgroundColor: theme.palette.grey[100]
  }),
  uzmanlikLabel: (theme) => ({
    fontWeight: 600,
    fontSize: 12,
    marginRight: '2px',
    color: theme.palette.text.secondary
  }),
  uzmanlikChip: {
    marginLeft: '2px'
  },
  detayWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  fieldPill: (theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    marginTop: '4px',
    marginRight: '6px',
    borderRadius: 12,
    backgroundColor: theme.palette.grey[100],
    fontSize: 12,
    maxWidth: '100%'
  }),
  fieldLabel: (theme) => ({
    fontWeight: 600,
    color: theme.palette.text.secondary
  }),
  fieldValue: (theme) => ({
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 280
  }),
  rowActions: {
    display: 'flex',
    alignItems: 'center'
  },
  emptyChild: (theme) => ({
    paddingLeft: theme.spacing(9),
    color: theme.palette.text.secondary,
    fontStyle: 'italic'
  }),
  fotoWrap: (theme) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  }),
  fotoThumb: (theme) => ({
    position: 'relative',
    width: 84,
    height: 84,
    borderRadius: 4,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[100]
  }),
  fotoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  fotoDel: {
    position: 'absolute',
    top: 0,
    right: 0,
    padding: '4px',
    zIndex: 2,
    backgroundColor: 'rgba(0,0,0,0.55)',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.75)'
    }
  },
  fotoBox: (theme) => ({
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  })
};

const emptyForm = {
  Id: 0,
  UstGrupId: '',
  GrupId: '',
  Aciklama: '',
  Description: '',
  StokNo: '',
  Marka: '',
  Model: '',
  Uzmanliklar: [], // seçili uzmanlık option nesneleri
  KalibrasyonIhtiyaci: false,
  KalibrasyonGun: '', // gün sayısı
  Active: true
};

// Autocomplete listesini her zaman aşağı doğru aç (yukarı flip etme)
const AsagiPopper = props => (
  <Popper
    {...props}
    placement="bottom-start"
    modifiers={{ flip: { enabled: false } }}
  />
);

// Dosyayı data-URL (base64) olarak oku
const fileToDataUrl = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const IZIN_VERILEN_TIPLER = ['image/jpeg', 'image/png'];
const MAX_FOTO_BYTE = 10 * 1024 * 1024; // 10 MB

// Ağaçta ekipmanın fotoğrafını gösteren küçük thumbnail (tembel yükleme:
// yalnızca grup açıldığında / satır render olduğunda çekilir)
function FotoThumb({ ekipmanId, onClick, refreshKey }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    if (refreshKey) setUrl(null); // yenilemede eski görseli temizle
    axios
      .get(`/GetEkipmanDosyalar/${ekipmanId}`)
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        // Ürün görseli: önce kapak, yoksa türü FOTO olan ilk belge (yoksa gösterme)
        const foto = list.find(x => x.Kapak) || list.find(x => (x.Tip || '').toUpperCase() === 'FOTO');
        if (!foto) return null;
        return axios.get(`/GetEkipmanDosya/${foto.Id}`).then(r2 => {
          const d = r2.data || {};
          if (alive && d.FileData) {
            setUrl(
              `data:${d.ContentType ||
                foto.ContentType ||
                'image/jpeg'};base64,${d.FileData}`
            );
          }
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [ekipmanId, refreshKey]);

  if (!url) return null;
  return (
    <img
      src={url}
      alt=""
      title="Büyütmek için tıklayın"
      onClick={e => {
        e.stopPropagation();
        if (onClick) onClick(url);
      }}
      style={{
        width: 40,
        height: 40,
        objectFit: 'cover',
        borderRadius: 4,
        marginRight: 8,
        verticalAlign: 'middle',
        cursor: 'pointer'
      }}
    />
  );
}

function EkipmanAdminYonetimi() {
  const [loading, setLoading] = useState(false);
  const [ustGruplar, setUstGruplar] = useState([]);
  const [gruplar, setGruplar] = useState([]);
  const [ekipmanlar, setEkipmanlar] = useState([]);
  const [uzmanliklar, setUzmanliklar] = useState([]); // ana liste
  const [ekipmanUzmanliklar, setEkipmanUzmanliklar] = useState([]); // bağlantılar
  const [mevcutFotolar, setMevcutFotolar] = useState([]); // düzenlemede yüklü fotoğraflar
  const [yeniFotolar, setYeniFotolar] = useState([]); // kayıtla birlikte yüklenecekler
  const [kapakYeniUid, setKapakYeniUid] = useState(null); // yeni dosyalardan kapak
  const [fotoRefresh, setFotoRefresh] = useState(0); // ağaç thumbnail'larını yenileme tetiği
  const [bigFoto, setBigFoto] = useState({ open: false, url: null }); // ağaçta foto büyütme
  const [galeri, setGaleri] = useState({ open: false, baslik: '', loading: false, items: [] }); // belge galerisi

  const [showPassive, setShowPassive] = useState(false);
  const [search, setSearch] = useState('');
  // Ekipman Yönetimi'nde görünecek rol (yönetici ↔ servis). Yalnız gerçek yönetici servise inebilir;
  // admin olmayan bu seçimle yetki yükseltemez (etki Ekipman Yönetimi mount'unda uygulanır).
  const navigate = useNavigate();
  const [rolOverride, setRolOverride] = useState(() => {
    try { return localStorage.getItem('EKIPMAN_ROL_OVERRIDE') || ''; } catch (e) { return ''; }
  });
  const setRol = v => {
    try { localStorage.setItem('EKIPMAN_ROL_OVERRIDE', v); } catch (e) { /* yoksay */ }
    setRolOverride(v);
    notify('info', (v === 'admin' ? 'Yönetici' : 'Servis') + ' rolüyle Ekipman Yönetimi açılıyor…');
    // rol farkı Ekipman Yönetimi sayfasında görünür — oraya git (taze mount → override okunur)
    setTimeout(() => navigate('/EkipmanYonetimi'), 400);
  };
  const [expandedUst, setExpandedUst] = useState({});
  const [expandedGrup, setExpandedGrup] = useState({});

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLevel, setDialogLevel] = useState('ust'); // 'ust' | 'grup' | 'ekipman'
  const [form, setForm] = useState(emptyForm);

  // Snackbar
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackType, setSnackType] = useState('success');
  const [snackMessage, setSnackMessage] = useState('');

  // Onay dialog'u (MUI - edit dialog'un üstünde güvenli şekilde açılır)
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const askConfirm = (title, message, onConfirm) =>
    setConfirmState({ open: true, title, message, onConfirm });
  const closeConfirm = () => setConfirmState(s => ({ ...s, open: false }));

  useEffect(() => {
    fetchAll();
  }, []);

  const notify = (type, message) => {
    setSnackType(type);
    setSnackMessage(message);
    setSnackOpen(true);
  };

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      axios.get('/GetEkipmanUstGruplar'),
      axios.get('/GetEkipmanGruplar'),
      axios.get('/GetEkipmanlar'),
      axios.get('/GetUzmanliklar'),
      axios.get('/GetEkipmanUzmanliklar')
    ])
      .then(([ustRes, grupRes, ekipRes, uzmRes, ekipUzmRes]) => {
        setUstGruplar(Array.isArray(ustRes.data) ? ustRes.data : []);
        setGruplar(Array.isArray(grupRes.data) ? grupRes.data : []);
        setEkipmanlar(Array.isArray(ekipRes.data) ? ekipRes.data : []);
        setUzmanliklar(Array.isArray(uzmRes.data) ? uzmRes.data : []);
        setEkipmanUzmanliklar(
          Array.isArray(ekipUzmRes.data) ? ekipUzmRes.data : []
        );
      })
      .catch(() => {
        notify('error', 'Veriler yüklenirken bir hata oluştu.');
      })
      .finally(() => setLoading(false));
  };

  const visible = item => showPassive || item.Active;

  // ---- üstte arama: ekipmanın hangi üst grup/grup altında olduğunu bulmak için ----
  const norm = s => String(s || '').toLocaleLowerCase('tr-TR')
    .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
    .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]/g, '');
  const q = norm(search);
  const eqMatch = eq => !q || norm(eq.Description).includes(q) || norm(eq.StokNo).includes(q);

  // Bir ekipmana bağlı uzmanlık bağlantıları
  const uzmanlikForEkipman = ekipmanId =>
    ekipmanUzmanliklar.filter(x => x.EkipmanId === ekipmanId);

  // Düzenlemede ekipmanın mevcut belgelerini (metadata) yükle
  const loadFotolar = ekipmanId => {
    axios
      .get(`/GetEkipmanDosyalar/${ekipmanId}`)
      .then(res => setMevcutFotolar(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMevcutFotolar([]));
  };

  // Belgeyi büyük göster (yeni: base64/url; mevcut: sunucudan çek)
  const gosterAdminDosya = (kaynak, item) => {
    if (kaynak === 'yeni') {
      if (item.url && /^image\//i.test(item.contentType || '')) setBigFoto({ open: true, url: item.url });
      else if (item.url) window.open(item.url, '_blank');
      return;
    }
    axios.get(`/GetEkipmanDosya/${item.Id}`).then(res => {
      const d = res.data || {};
      const url = d.FileData ? `data:${d.ContentType || 'image/jpeg'};base64,${d.FileData}` : null;
      if (!url) { notify('info', 'Belge getirilemedi.'); return; }
      if (/^image\//i.test(d.ContentType || 'image/jpeg')) setBigFoto({ open: true, url });
      else window.open(url, '_blank');
    }).catch(() => notify('error', 'Belge getirilirken hata oluştu.'));
  };

  // Ekipmanın tüm belgelerini galeri olarak aç
  const openAdminGaleri = eq => {
    setGaleri({ open: true, baslik: eq.Description || 'Ekipman', loading: true, items: [] });
    axios.get(`/GetEkipmanDosyalar/${eq.Id}`)
      .then(async res => {
        const list = Array.isArray(res.data) ? res.data : [];
        const withData = await Promise.all(list.map(async m => {
          try {
            const r = await axios.get(`/GetEkipmanDosya/${m.Id}`);
            const d = r.data || {};
            const ct = d.ContentType || m.ContentType || '';
            return { ...m, ContentType: ct, dataUrl: d.FileData ? `data:${ct || 'image/jpeg'};base64,${d.FileData}` : null };
          } catch (e) { return { ...m, dataUrl: null }; }
        }));
        setGaleri({ open: true, baslik: eq.Description || 'Ekipman', loading: false, items: withData });
      })
      .catch(() => setGaleri({ open: true, baslik: eq.Description || 'Ekipman', loading: false, items: [] }));
  };

  // Kapak (ana görsel) seç
  const onKapakSecAdmin = (kaynak, item) => {
    if (kaynak === 'yeni') {
      setKapakYeniUid(item.uid);
      setMevcutFotolar(prev => prev.map(m => ({ ...m, Kapak: false })));
      return;
    }
    setKapakYeniUid(null);
    if (!form.Id) return;
    axios.get(`/SetEkipmanKapak/${form.Id}/${item.Id}`)
      .then(() => {
        setMevcutFotolar(prev => prev.map(m => ({ ...m, Kapak: m.Id === item.Id })));
        setFotoRefresh(v => v + 1); // ağaç küçük görseli güncellensin
        notify('success', 'Ana görsel güncellendi.');
      })
      .catch(() => notify('error', 'Kapak güncellenemedi.'));
  };

  const handleDeleteFoto = id => {
    askConfirm(
      'Fotoğrafı Sil',
      'Bu fotoğrafı silmek istediğinize emin misiniz?',
      () => {
        setLoading(true);
        axios
          .get(`/DeleteEkipmanDosya/${id}`)
          .then(res => {
            if (res.data && res.data.ErrorCode === '0') {
              setMevcutFotolar(p => p.filter(x => x.Id !== id));
              setFotoRefresh(v => v + 1);
              notify('success', 'Fotoğraf silindi.');
            } else {
              notify(
                'error',
                (res.data && res.data.ErrorDescription) ||
                  'Fotoğraf silinemedi.'
              );
            }
          })
          .catch(() => notify('error', 'Silme sırasında hata oluştu.'))
          .finally(() => setLoading(false));
      }
    );
  };

  const toggleUst = id => setExpandedUst(p => ({ ...p, [id]: !p[id] }));
  const toggleGrup = id => setExpandedGrup(p => ({ ...p, [id]: !p[id] }));

  // ---- Dialog açıcılar ----
  const openAddUst = () => {
    setDialogLevel('ust');
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };
  const openEditUst = row => {
    setDialogLevel('ust');
    setForm({
      ...emptyForm,
      Id: row.Id,
      Aciklama: row.Aciklama,
      Active: row.Active
    });
    setDialogOpen(true);
  };
  const openAddGrup = ustGrupId => {
    setDialogLevel('grup');
    setForm({ ...emptyForm, UstGrupId: ustGrupId });
    setDialogOpen(true);
  };
  const openEditGrup = row => {
    setDialogLevel('grup');
    setForm({
      ...emptyForm,
      Id: row.Id,
      UstGrupId: row.UstGrupId,
      Aciklama: row.Aciklama,
      Active: row.Active
    });
    setDialogOpen(true);
  };
  const openAddEkipman = (ustGrupId, grupId) => {
    setDialogLevel('ekipman');
    setForm({ ...emptyForm, UstGrupId: ustGrupId, GrupId: grupId });
    setMevcutFotolar([]);
    setYeniFotolar([]);
    setKapakYeniUid(null);
    setDialogOpen(true);
  };
  const openEditEkipman = row => {
    setDialogLevel('ekipman');
    const seciliIdler = uzmanlikForEkipman(row.Id).map(x => x.UzmanlikId);
    const seciliUzmanliklar = uzmanliklar.filter(u =>
      seciliIdler.includes(u.Kod)
    );
    setForm({
      ...emptyForm,
      Id: row.Id,
      UstGrupId: row.UstGrupId,
      GrupId: row.GrupId,
      Description: row.Description,
      StokNo: row.StokNo || '',
      Aciklama: row.Aciklama || '',
      Marka: row.Marka || '',
      Model: row.Model || '',
      Uzmanliklar: seciliUzmanliklar,
      KalibrasyonIhtiyaci: !!row.KalibrasyonIhtiyaci,
      KalibrasyonGun:
        row.KalibrasyonGun != null ? String(row.KalibrasyonGun) : '',
      Active: row.Active
    });
    setMevcutFotolar([]);
    setYeniFotolar([]);
    setKapakYeniUid(null);
    loadFotolar(row.Id);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const setFormField = (field, value) =>
    setForm(p => ({ ...p, [field]: value }));

  // Dialog'da üst grup değişince grup seçimini sıfırla
  const handleUstChangeInDialog = value => {
    setForm(p => ({ ...p, UstGrupId: value, GrupId: '' }));
  };

  const dialogTitle = () => {
    const ad = { ust: 'Üst Grup', grup: 'Grup', ekipman: 'Ekipman' }[
      dialogLevel
    ];
    return `${form.Id > 0 ? 'Düzenle' : 'Yeni'} - ${ad}`;
  };

  const handleSave = () => {
    let url;
    let payload;

    if (dialogLevel === 'ust') {
      if (!form.Aciklama.trim()) return notify('error', 'Açıklama boş olamaz.');
      url = '/SetEkipmanUstGrup';
      payload = {
        Id: form.Id,
        Aciklama: form.Aciklama.trim(),
        Active: form.Active
      };
    } else if (dialogLevel === 'grup') {
      if (!form.UstGrupId) return notify('error', 'Üst grup seçilmelidir.');
      if (!form.Aciklama.trim()) return notify('error', 'Açıklama boş olamaz.');
      url = '/SetEkipmanGrup';
      payload = {
        Id: form.Id,
        UstGrupId: Number(form.UstGrupId),
        Aciklama: form.Aciklama.trim(),
        Active: form.Active
      };
    } else {
      if (!form.UstGrupId) return notify('error', 'Üst grup seçilmelidir.');
      if (!form.GrupId) return notify('error', 'Grup seçilmelidir.');
      if (!form.Description.trim())
        return notify('error', 'Donanım adı boş olamaz.');
      if (tipEksikVar(yeniFotolar))
        return notify('warning', 'Lütfen dosya türünü seçiniz.');

      url = '/SetEkipman';
      payload = {
        Id: form.Id,
        UstGrupId: Number(form.UstGrupId),
        GrupId: Number(form.GrupId),
        Description: form.Description.trim(),
        StokNo: form.StokNo ? form.StokNo.trim() : '',
        Aciklama: form.Aciklama ? form.Aciklama.trim() : '',
        Marka: form.Marka ? form.Marka.trim() : '',
        Model: form.Model ? form.Model.trim() : '',
        KalibrasyonIhtiyaci: form.KalibrasyonIhtiyaci,
        KalibrasyonGun:
          form.KalibrasyonIhtiyaci && form.KalibrasyonGun !== ''
            ? Number(form.KalibrasyonGun)
            : null,
        Active: form.Active,
        UzmanlikIds: (form.Uzmanliklar || []).map(u => u.Kod)
      };
    }

    setLoading(true);
    axios
      .post(url, payload)
      .then(async res => {
        if (res.data && res.data.ErrorCode === '0') {
          let fotoHata = false;
          // Ekipman kaydı sonrası yeni fotoğrafları yükle (kayıtla birlikte)
          if (dialogLevel === 'ekipman' && yeniFotolar.length > 0) {
            const ekipmanId = res.data.Id || form.Id;
            try {
              // kapak: seçili yeni; yoksa mevcutta kapak yoksa ilk yeni foto
              const mevcutKapakVar = mevcutFotolar.some(m => m.Kapak);
              let kUid = kapakYeniUid;
              if (!kUid && !mevcutKapakVar) {
                const ilk = yeniFotolar.find(f => f.tip === 'FOTO') || yeniFotolar.find(f => /^image\//i.test(f.contentType || ''));
                if (ilk) kUid = ilk.uid;
              }
              await Promise.all(
                yeniFotolar.map(f =>
                  axios.post('/UploadEkipmanDosya', {
                    EkipmanId: ekipmanId,
                    DosyaAdi: f.ad,
                    ContentType: f.contentType,
                    Tip: f.tip,
                    Kapak: f.uid === kUid,
                    FileData: f.base64
                  })
                )
              );
            } catch (err) {
              fotoHata = true;
            }
          }
          notify(
            fotoHata ? 'error' : 'success',
            fotoHata
              ? 'Ekipman kaydedildi ancak bazı belgeler yüklenemedi.'
              : res.data.ErrorDescription || 'İşlem başarılı.'
          );
          setDialogOpen(false);
          fetchAll();
          setFotoRefresh(v => v + 1);
        } else {
          notify(
            'error',
            (res.data && res.data.ErrorDescription) || 'İşlem başarısız.'
          );
        }
      })
      .catch(() => notify('error', 'Kayıt sırasında bir hata oluştu.'))
      .finally(() => setLoading(false));
  };

  const handleDelete = (level, id) => {
    const urlMap = {
      ust: `/DeleteEkipmanUstGrup/${id}`,
      grup: `/DeleteEkipmanGrup/${id}`,
      ekipman: `/DeleteEkipman/${id}`
    };

    askConfirm(
      'Pasifleştir',
      'Bu kaydı pasif hale getirmek istediğinize emin misiniz?',
      () => {
        setLoading(true);
        axios
          .get(urlMap[level])
          .then(res => {
            if (res.data && res.data.ErrorCode === '0') {
              notify(
                'success',
                res.data.ErrorDescription || 'Kayıt pasif hale getirildi.'
              );
              fetchAll();
            } else {
              notify(
                'error',
                (res.data && res.data.ErrorDescription) || 'İşlem başarısız.'
              );
            }
          })
          .catch(() => notify('error', 'İşlem sırasında bir hata oluştu.'))
          .finally(() => setLoading(false));
      }
    );
  };

  // Pasif kaydı tekrar aktifleştir
  const handleReactivate = (level, row) => {
    let url;
    let payload;
    if (level === 'ust') {
      url = '/SetEkipmanUstGrup';
      payload = { Id: row.Id, Aciklama: row.Aciklama, Active: true };
    } else if (level === 'grup') {
      url = '/SetEkipmanGrup';
      payload = {
        Id: row.Id,
        UstGrupId: row.UstGrupId,
        Aciklama: row.Aciklama,
        Active: true
      };
    } else {
      url = '/SetEkipman';
      payload = {
        Id: row.Id,
        UstGrupId: row.UstGrupId,
        GrupId: row.GrupId,
        Description: row.Description,
        StokNo: row.StokNo || '',
        Aciklama: row.Aciklama || '',
        Marka: row.Marka || '',
        Model: row.Model || '',
        KalibrasyonIhtiyaci: !!row.KalibrasyonIhtiyaci,
        KalibrasyonGun: row.KalibrasyonIhtiyaci ? row.KalibrasyonGun : null,
        Active: true,
        UzmanlikIds: uzmanlikForEkipman(row.Id).map(x => x.UzmanlikId)
      };
    }
    setLoading(true);
    axios
      .post(url, payload)
      .then(res => {
        if (res.data && res.data.ErrorCode === '0') {
          notify('success', 'Kayıt aktif hale getirildi.');
          fetchAll();
        } else {
          notify(
            'error',
            (res.data && res.data.ErrorDescription) || 'İşlem başarısız.'
          );
        }
      })
      .catch(() => notify('error', 'İşlem sırasında bir hata oluştu.'))
      .finally(() => setLoading(false));
  };

  // Dialog'da grup seçimi için ilgili üst gruba bağlı AKTİF gruplar
  const dialogGruplar = gruplar.filter(
    g => g.Active && String(g.UstGrupId) === String(form.UstGrupId)
  );

  const renderRowActions = (level, row, onAdd) => (
    <div style={styles.rowActions}>
      {onAdd && (
        <Tooltip title={level === 'ust' ? 'Grup Ekle' : 'Ekipman Ekle'}>
          <IconButton size="small" color="primary" onClick={onAdd}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Düzenle">
        <IconButton size="small" onClick={() => row.onEdit()}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {row.Active ? (
        <Tooltip title="Pasifleştir">
          <IconButton size="small" onClick={() => handleDelete(level, row.Id)}>
            <DeleteIcon fontSize="small" color="error" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Aktifleştir">
          <IconButton size="small" onClick={() => handleReactivate(level, row)}>
            <RestoreFromTrashIcon fontSize="small" color="primary" />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );

  return (
    <Page sx={styles.root} title="Ekipman Admin Yönetimi">
      <Backdrop open={loading} style={{ zIndex: 2000, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <AlertScreen
          variant={snackType}
          message={snackMessage}
          onClose={() => setSnackOpen(false)}
        />
      </Snackbar>

      <Card>
        <CardHeader
          avatar={<CategoryIcon color="primary" />}
          title="Ekipman Admin Yönetimi"
          subheader="Üst grup → Grup → Ekipman (stok no) ağaç yapısını yönetin"
          action={
            /* Rol değiştir: Ekipman Yönetimi'ni yönetici ya da servis görünümünde aç */
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, mr: 1, px: 1, py: 0.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', backgroundColor: 'action.hover' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark' }}>Rol:</Typography>
              <Chip size="small" label="Yönetici" clickable
                color={rolOverride === 'admin' ? 'secondary' : 'default'}
                variant={rolOverride === 'admin' ? 'filled' : 'outlined'}
                onClick={() => setRol('admin')} />
              <Chip size="small" label="Servis" clickable
                color={rolOverride === 'servis' ? 'secondary' : 'default'}
                variant={rolOverride === 'servis' ? 'filled' : 'outlined'}
                onClick={() => setRol('servis')} />
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={styles.toolbar}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={openAddUst}
            >
              Yeni Üst Grup
            </Button>
            <TextField
              size="small"
              placeholder="Ekipman ara: ad / stok no — hangi grupta?"
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ minWidth: 300, ml: 2 }}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <Tooltip title="Aramayı temizle">
                      <IconButton size="small" edge="end" onClick={() => setSearch('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null
              }}
            />
            <div style={styles.spacer} />
            <FormControlLabel
              control={
                <Switch
                  checked={showPassive}
                  onChange={e => setShowPassive(e.target.checked)}
                  color="primary"
                />
              }
              label="Pasifleri göster"
            />
          </Box>

          <List disablePadding>
            {ustGruplar.filter(visible).length === 0 && (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: 16 }}
              >
                Kayıt bulunamadı.
              </Typography>
            )}

            {q && !ekipmanlar.some(eq => visible(eq) && eqMatch(eq)) && (
              <Typography variant="body2" color="textSecondary" style={{ padding: 16 }}>
                “{search}” ile eşleşen ekipman bulunamadı.
              </Typography>
            )}

            {ustGruplar.filter(visible).map(ust => {
              const childGruplar = gruplar
                .filter(g => g.UstGrupId === ust.Id)
                .filter(visible)
                .filter(g => !q || ekipmanlar.some(eq => eq.GrupId === g.Id && visible(eq) && eqMatch(eq)));
              if (q && childGruplar.length === 0) return null; // aramada eşleşme yoksa gizle
              const ustOpen = q ? true : !!expandedUst[ust.Id];

              return (
                <React.Fragment key={`ust-${ust.Id}`}>
                  <ListItemButton
                    sx={[styles.ustRow, !ust.Active && styles.passive]}
                    onClick={() => toggleUst(ust.Id)}
                  >
                    {ustOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                    <ListItemText
                      primary={<strong>{ust.Aciklama}</strong>}
                      secondary={!ust.Active ? 'Pasif' : null}
                    />
                    <div onClick={e => e.stopPropagation()}>
                      {renderRowActions(
                        'ust',
                        { ...ust, onEdit: () => openEditUst(ust) },
                        () => openAddGrup(ust.Id)
                      )}
                    </div>
                  </ListItemButton>

                  <Collapse in={ustOpen} timeout="auto" unmountOnExit>
                    {childGruplar.length === 0 && (
                      <Typography
                        variant="body2"
                        sx={styles.emptyChild}
                      >
                        Bu üst gruba ait grup yok.
                      </Typography>
                    )}
                    {childGruplar.map(grup => {
                      const childEkipmanlar = ekipmanlar
                        .filter(eq => eq.GrupId === grup.Id)
                        .filter(visible)
                        .filter(eqMatch);
                      const grupOpen = q ? true : !!expandedGrup[grup.Id];

                      return (
                        <React.Fragment key={`grup-${grup.Id}`}>
                          <ListItemButton
                            sx={[styles.grupRow, !grup.Active && styles.passive]}
                            onClick={() => toggleGrup(grup.Id)}
                          >
                            {grupOpen ? (
                              <ExpandMoreIcon />
                            ) : (
                              <ChevronRightIcon />
                            )}
                            <ListItemText
                              primary={grup.Aciklama}
                              secondary={!grup.Active ? 'Pasif' : null}
                            />
                            <div onClick={e => e.stopPropagation()}>
                              {renderRowActions(
                                'grup',
                                { ...grup, onEdit: () => openEditGrup(grup) },
                                () => openAddEkipman(grup.UstGrupId, grup.Id)
                              )}
                            </div>
                          </ListItemButton>

                          <Collapse in={grupOpen} timeout="auto" unmountOnExit>
                            {childEkipmanlar.length === 0 && (
                              <Typography
                                variant="body2"
                                sx={styles.emptyChild}
                              >
                                Bu gruba ait ekipman yok.
                              </Typography>
                            )}
                            {childEkipmanlar.map(eq => (
                              <ListItem
                                key={`ekipman-${eq.Id}`}
                                sx={[styles.ekipmanRow, !eq.Active && styles.passive]}
                              >
                                <ListItemText
                                  primary={
                                    <span
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                      }}
                                    >
                                      <FotoThumb
                                        ekipmanId={eq.Id}
                                        refreshKey={fotoRefresh}
                                        onClick={u =>
                                          setBigFoto({ open: true, url: u })
                                        }
                                      />
                                      <strong>{eq.Description}</strong>
                                      <Tooltip title="Belgeleri gör">
                                        <IconButton size="small" style={{ padding: 2, marginLeft: 4 }}
                                          onClick={e => { e.stopPropagation(); openAdminGaleri(eq); }}>
                                          <VisibilityIcon fontSize="small" color="action" />
                                        </IconButton>
                                      </Tooltip>
                                    </span>
                                  }
                                  secondaryTypographyProps={{
                                    component: 'div'
                                  }}
                                  secondary={
                                    <div style={styles.detayWrap}>
                                      {!eq.Active ? (
                                        <Box component="span" sx={styles.fieldPill}>
                                          <Box component="span" sx={styles.fieldLabel}>
                                            Durum:
                                          </Box>
                                          <Box
                                            component="span"
                                            sx={[styles.fieldValue, { color: '#c62828' }]}
                                          >
                                            Pasif
                                          </Box>
                                        </Box>
                                      ) : null}
                                      {eq.Marka ? (
                                        <Box component="span" sx={styles.fieldPill}>
                                          <Box component="span" sx={styles.fieldLabel}>
                                            Marka:
                                          </Box>
                                          <Box component="span" sx={styles.fieldValue}>
                                            {eq.Marka}
                                          </Box>
                                        </Box>
                                      ) : null}
                                      {eq.Model ? (
                                        <Box component="span" sx={styles.fieldPill}>
                                          <Box component="span" sx={styles.fieldLabel}>
                                            Model:
                                          </Box>
                                          <Box component="span" sx={styles.fieldValue}>
                                            {eq.Model}
                                          </Box>
                                        </Box>
                                      ) : null}
                                      {eq.StokNo ? (
                                        <Box component="span" sx={styles.fieldPill}>
                                          <Box component="span" sx={styles.fieldLabel}>
                                            Stok Kodu:
                                          </Box>
                                          <Box component="span" sx={styles.fieldValue}>
                                            {eq.StokNo}
                                          </Box>
                                        </Box>
                                      ) : null}
                                      {eq.Aciklama ? (
                                        <Box component="span" sx={styles.fieldPill}>
                                          <Box component="span" sx={styles.fieldLabel}>
                                            Açıklama:
                                          </Box>
                                          <Box
                                            component="span"
                                            sx={styles.fieldValue}
                                            title={eq.Aciklama}
                                          >
                                            {eq.Aciklama}
                                          </Box>
                                        </Box>
                                      ) : null}
                                      {eq.KalibrasyonIhtiyaci ? (
                                        <Box component="span" sx={styles.fieldPill}>
                                          <Box component="span" sx={styles.fieldLabel}>
                                            Kalibrasyon:
                                          </Box>
                                          <Box component="span" sx={styles.fieldValue}>
                                            {`Gerekli${
                                              eq.KalibrasyonGun != null
                                                ? ` • ${eq.KalibrasyonGun} gün`
                                                : ''
                                            }`}
                                          </Box>
                                        </Box>
                                      ) : null}
                                      {uzmanlikForEkipman(eq.Id).length > 0 && (
                                        <Box component="span" sx={styles.uzmanlikGroup}>
                                          <Box
                                            component="span"
                                            sx={styles.uzmanlikLabel}
                                          >
                                            Uzmanlıklar:
                                          </Box>
                                          {uzmanlikForEkipman(eq.Id).map(x => (
                                            <Chip
                                              key={x.UzmanlikId}
                                              size="small"
                                              color="primary"
                                              variant="outlined"
                                              sx={styles.uzmanlikChip}
                                              label={x.Aciklama || x.UzmanlikId}
                                            />
                                          ))}
                                        </Box>
                                      )}
                                    </div>
                                  }
                                />
                                {renderRowActions('ekipman', {
                                  ...eq,
                                  onEdit: () => openEditEkipman(eq)
                                })}
                              </ListItem>
                            ))}
                          </Collapse>
                        </React.Fragment>
                      );
                    })}
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        </CardContent>
      </Card>

      {/* Ekle / Düzenle Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{dialogTitle()}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {dialogLevel === 'grup' && (
              <Grid size={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Üst Grup</InputLabel>
                  <Select
                    label="Üst Grup"
                    value={form.UstGrupId}
                    onChange={e => setFormField('UstGrupId', e.target.value)}
                  >
                    {ustGruplar
                      .filter(u => u.Active)
                      .map(u => (
                        <MenuItem key={u.Id} value={u.Id}>
                          {u.Aciklama}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {dialogLevel === 'ekipman' && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Üst Grup</InputLabel>
                    <Select
                      label="Üst Grup"
                      value={form.UstGrupId}
                      onChange={e => handleUstChangeInDialog(e.target.value)}
                    >
                      {ustGruplar
                        .filter(u => u.Active)
                        .map(u => (
                          <MenuItem key={u.Id} value={u.Id}>
                            {u.Aciklama}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={!form.UstGrupId}
                  >
                    <InputLabel>Grup</InputLabel>
                    <Select
                      label="Grup"
                      value={form.GrupId}
                      onChange={e => setFormField('GrupId', e.target.value)}
                    >
                      {dialogGruplar.map(g => (
                        <MenuItem key={g.Id} value={g.Id}>
                          {g.Aciklama}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid size={12}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label={dialogLevel === 'ekipman' ? 'Donanım Adı' : 'Açıklama'}
                value={
                  dialogLevel === 'ekipman' ? form.Description : form.Aciklama
                }
                inputProps={{
                  maxLength: dialogLevel === 'ekipman' ? 100 : 400
                }}
                onChange={e =>
                  setFormField(
                    dialogLevel === 'ekipman' ? 'Description' : 'Aciklama',
                    e.target.value
                  )
                }
              />
            </Grid>

            {dialogLevel === 'ekipman' && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Marka"
                    value={form.Marka}
                    inputProps={{ maxLength: 200 }}
                    onChange={e => setFormField('Marka', e.target.value)}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Model"
                    value={form.Model}
                    inputProps={{ maxLength: 200 }}
                    onChange={e => setFormField('Model', e.target.value)}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Stok Kodu"
                    value={form.StokNo}
                    inputProps={{ maxLength: 60 }}
                    onChange={e => setFormField('StokNo', e.target.value)}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Açıklama"
                    value={form.Aciklama}
                    inputProps={{ maxLength: 500 }}
                    onChange={e => setFormField('Aciklama', e.target.value)}
                  />
                </Grid>

                <Grid size={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={uzmanliklar}
                    value={form.Uzmanliklar}
                    PopperComponent={AsagiPopper}
                    getOptionLabel={o => o.Aciklama || ''}
                    isOptionEqualToValue={(o, v) => o.Kod === v.Kod}
                    onChange={(e, val) => setFormField('Uzmanliklar', val)}
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Uzmanlık (opsiyonel)"
                        placeholder="Uzmanlık ara / seç"
                      />
                    )}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.KalibrasyonIhtiyaci}
                        color="primary"
                        onChange={e =>
                          setFormField('KalibrasyonIhtiyaci', e.target.checked)
                        }
                      />
                    }
                    label="Kalibrasyon İhtiyacı"
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.Active}
                        color="primary"
                        onChange={e => setFormField('Active', e.target.checked)}
                      />
                    }
                    label="Aktif"
                  />
                </Grid>

                {form.KalibrasyonIhtiyaci && (
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="number"
                      label="Kalibrasyon Süresi (Gün) (opsiyonel)"
                      placeholder="örn. 365"
                      value={form.KalibrasyonGun}
                      inputProps={{ min: 0 }}
                      onChange={e =>
                        setFormField(
                          'KalibrasyonGun',
                          e.target.value.replace(/[^0-9]/g, '')
                        )
                      }
                    />
                  </Grid>
                )}

                <Grid size={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Belgeler
                  </Typography>
                  <DosyaYukleyici
                    hint={DOSYA_HINT}
                    yeni={yeniFotolar}
                    setYeni={setYeniFotolar}
                    mevcut={mevcutFotolar}
                    onMevcutSil={handleDeleteFoto}
                    onGoster={gosterAdminDosya}
                    kapakYeniUid={kapakYeniUid}
                    onKapakSec={onKapakSecAdmin}
                    tipZorunlu
                  />
                </Grid>
              </>
            )}

            {dialogLevel !== 'ekipman' && (
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.Active}
                      onChange={e => setFormField('Active', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Aktif"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>İptal</Button>
          <Button variant="contained" color="secondary" onClick={handleSave}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Onay Dialog (silme / pasifleştirme) */}
      <Dialog
        open={confirmState.open}
        onClose={closeConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmState.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirmState.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Hayır</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              const fn = confirmState.onConfirm;
              closeConfirm();
              if (fn) fn();
            }}
          >
            Evet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fotoğraf büyütme */}
      <Dialog
        open={bigFoto.open}
        onClose={() => setBigFoto({ open: false, url: null })}
        maxWidth="md"
      >
        <DialogContent style={{ padding: 8 }}>
          {bigFoto.url && (
            <img
              src={bigFoto.url}
              alt="Ekipman fotoğrafı"
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '80vh',
                margin: '0 auto'
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBigFoto({ open: false, url: null })}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Belge galerisi */}
      <Dialog
        open={galeri.open}
        onClose={() => setGaleri(g => ({ ...g, open: false }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{galeri.baslik} — Belgeler</DialogTitle>
        <DialogContent dividers>
          {galeri.loading ? (
            <Typography variant="body2" color="textSecondary">Belgeler yükleniyor…</Typography>
          ) : galeri.items.length === 0 ? (
            <Typography variant="body2" color="textSecondary">Bu ekipmana ait belge yok.</Typography>
          ) : (
            <Grid container spacing={2}>
              {galeri.items.map((it, i) => {
                const img = /^image\//i.test(it.ContentType || '');
                const ac = () => {
                  if (img && it.dataUrl) setBigFoto({ open: true, url: it.dataUrl });
                  else if (it.dataUrl) window.open(it.dataUrl, '_blank');
                };
                return (
                  <Grid key={it.Id || i} size={{ xs: 6, sm: 4, md: 3 }}>
                    <Box onClick={ac} style={{ cursor: 'pointer', border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
                      <Box style={{ height: 120, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {img && it.dataUrl ? (
                          <img src={it.dataUrl} alt={it.DosyaAdi} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                          <CategoryIcon style={{ fontSize: 40, opacity: 0.4 }} />
                        )}
                      </Box>
                      <Box style={{ padding: 6 }}>
                        <Chip size="small" color="secondary" label={dosyaTipiLabel(it.Tip)} style={{ height: 18 }} />
                        {it.Kapak ? <Chip size="small" label="kapak" style={{ height: 18, marginLeft: 4, background: '#fff8e1', color: '#f57f17' }} /> : null}
                        <div style={{ fontSize: 12, color: '#555', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {it.DosyaAdi || 'Belge'}
                        </div>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGaleri(g => ({ ...g, open: false }))}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

export default EkipmanAdminYonetimi;
